/**
 * Format a numeric amount to a currency string
 * @param amount The amount to format (in cents for USD, in base unit for others)
 * @param currency The currency code (defaults to USD if not provided)
 * @returns Formatted currency string
 */
export const formatPrice = (amount: number, currency: string = 'USD') => {
    try {
        // USD amounts are in cents, need to divide by 100
        // Other currencies (KRW, IDR, etc.) are already in base unit
        const displayAmount = currency === 'USD' ? amount / 100 : amount;
        
        // Determine decimal places based on currency
        const minimumFractionDigits = currency === 'KRW' || currency === 'IDR' ? 0 : 0;
        const maximumFractionDigits = currency === 'KRW' || currency === 'IDR' ? 0 : 2;
        
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits,
            maximumFractionDigits
        }).format(displayAmount);
    } catch (error) {
        // Fallback formatting if currency is invalid
        const displayAmount = currency === 'USD' ? amount / 100 : amount;
        return `$${displayAmount.toFixed(2)}`;
    }
};

/**
 * Format a date string to a localized date string
 * @param dateString ISO date string to format
 * @param locale Locale to use for formatting (defaults to en-US)
 * @returns Formatted date string
 */
export const formatDate = (dateString: string, locale: string = 'en-US') => {
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString(locale);
    } catch (error) {
        return dateString;
    }
};

/**
 * Format a time string to a localized time string
 * @param timeString ISO time string or Date object to format
 * @param locale Locale to use for formatting (defaults to en-US)
 * @returns Formatted time string
 */
export const formatTime = (timeString: string | Date, locale: string = 'en-US') => {
    try {
        const time = typeof timeString === 'string' ? new Date(timeString) : timeString;
        return time.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
        return typeof timeString === 'string' ? timeString : timeString.toString();
    }
};